import { defineStore } from 'pinia';
import { useDonorStore } from '@/stores/DonorStore.js';
import api from '@/js/api.js';
import neonSSOAPI from '@/js/neonSSOAPI.js';

export const useAuthStore = defineStore('authStore', {
  state: () => ({
    authenticated: false,
    token: null,
    expires: null,
    id_token: null,
    refresh_token: null,
    client_id: null,
    login_referrer: null,
    authVariablesSet: false,
    localhostPortalDomain: 'donorportal.uat.ngd.computer',
    showAuthSwitchDialog: false,
    type: 'donorAccount',
    loginForm: null,
    signupForm: null,

    authenticationSystem: 'ngd-next-sso',
    authAPI: {
      'ngd-next-sso': api,
      'neon-sso': neonSSOAPI,
    },
  }),
  getters: {},
  actions: {
    async donorLogin(params = {}) {
      if (!params.email || !params.password) {
        params.email = this.loginForm.states.email.value;
        params.password = this.loginForm.states.password.value;
      }

      if (!params.email || !params.password) {
        throw { message: 'Could not authenticate you. Please try again.' };
      }

      try {
        const response = await api.post('authenticate', {
          email: params.email,
          password: params.password,
          redirect_uri: window.location.origin,
          hosts_id: useDonorStore().hosts_id,
          client_id: import.meta.env.VITE_OIDC_CLIENT_ID,
          response_type: 'code',
          scope: 'openid',
          state: Math.random().toString(36),
          nonce: Math.random().toString(36),
        });

        if (response.data.result == 'success') {
          const uri = new URL(response.data.redirect_uri);
          const code = uri.searchParams.get('code');

          if (params.companyAccount) {
            useDonorStore().skipOnAuthenticated = true;
          }

          if (await this.authenticateCode(code)) {
            if (params.companyAccount) {
              useDonorStore().skipOnAuthenticated = false;
              await this.switchAccount(params.email, params.password);
            }
            return true;
          } else {
            throw { message: 'Could not authenticate you. Please try again.' };
          }
        } else {
          throw { message: response.data?.message ?? 'Could not authenticate you. Please try again.' };
        }
      } catch (e) {
        throw e.response?.data?.message ? e.response.data : e;
      }
    },
    async donorSignup(params = {}) {
      if (!params.email || !params.password) {
        params.email = this.signupForm.states.email.value;
        params.password = this.signupForm.states.password.value;
        params.metadata = {
          firstName: this.signupForm.states.firstName.value,
          lastName: this.signupForm.states.lastName.value,
        };
      }

      if (!params.email || !params.password) {
        throw { message: 'Could not create your account. Please try again.' };
      }

      try {
        const response = await api.post(`sign-up`, {
          email: params.email,
          password: params.password,
          confirmPassword: params.password,
          hosts_id: useDonorStore().hosts_id,
          ...params.metadata,
        });

        if (response.data?.message && response.data.message == 'User created successfully') {
          return this.donorLogin({
            email: params.email,
            password: params.password,
            companyAccount: params.companyAccount,
          });
        } else {
          throw { message: response.data?.message ?? 'Could not create your account. Please try again.' };
        }
      } catch (e) {
        throw { message: e.response?.data?.message ?? 'Could not create your account. Please try again.' };
      }
    },

    login() {
      // This is only really for local testing. Login flow starts from login-widget or auth-guard.
      const oidcUrl = new URL(api.defaults.baseURL),
        cmsUrl = new URL('https://uat-demo-for-product-development.multiscreensite.com'),
        currentUrl = window.location;

      let params = {
        hosts_id: 19,
        redirect_uri: `${cmsUrl.origin}/authorize`,
        new_portal: `${currentUrl.origin}`,
      };
      if (window.location.hostname === 'localhost') {
        params = {
          hosts_id: 19,
          redirect_uri: `${currentUrl.origin}/authenticate`,
        };
      }

      window.location.href = `${oidcUrl.origin}/oidc?` + new URLSearchParams(params).toString();
    },

    async checkAuth() {
      if ('localStorage' in window && window['localStorage'] !== null) {
        // Check Via Local Storage
        await this.checkLocalStorage();
        setInterval(() => {
          this.checkLocalStorage();
        }, 500);
        if (this.token) {
          return true;
        }
      }
    },

    async checkLocalStorage() {
      const token = localStorage.getItem('token');
      const expires = localStorage.getItem('expires');

      if (token && expires > new Date().getTime() / 1000 && token !== this.token) {
        await this.authenticateFromStorage();
        return true;
      }

      if (token && expires > new Date().getTime() / 1000) {
        return true;
      }

      if (
        this.authenticated ||
        this.token ||
        this.expires ||
        this.id_token ||
        this.refresh_token ||
        this.client_id ||
        this.login_referrer
      ) {
        await this.setAuthVariables({
          authenticated: false,
          token: null,
          expires: null,
          id_token: null,
          refresh_token: null,
          client_id: null,
          login_referrer: null,
        });
      }

      return false;
    },

    async authenticateFromStorage() {
      await this.setAuthVariables({
        authenticated: localStorage.getItem('token') ? true : false,
        token: localStorage.getItem('token'),
        expires: localStorage.getItem('expires'),
        id_token: localStorage.getItem('id_token'),
        refresh_token: localStorage.getItem('refresh_token'),
        client_id: localStorage.getItem('client_id'),
        login_referrer: localStorage.getItem('login_referrer'),
      });

      this.startRefreshTokenTimer();
    },

    async startRefreshTokenTimer() {
      const expires = localStorage.getItem('expires');
      const time = expires - new Date().getTime() / 1000 - 60;
      setTimeout(() => this.refreshToken(), time * 1000);
    },

    async refreshToken() {
      try {
        const response = await api.post(`token`, {
          grant_type: 'refresh_token',
          refresh_token: this.refresh_token,
          client_id: this.client_id,
        });

        if (response.status === 200) {
          this.authenticateFromAPIResponse(response);
          return true;
        }
      } catch (error) {
        console.error('Error refreshing token:', error);
      }

      return false;
    },

    async authenticateCode(code, company_id = null) {
      let redirect_uri = window.location.origin;
      const urlParams = new URLSearchParams(window.location.search);
      if (this.authenticationSystem == 'neon-sso') {
        // Get the company parameter from the URL
        redirect_uri = `${window.location.origin}/authenticate?company_id=${urlParams.get('company_id')}`;
      }

      const data = new URLSearchParams({
          code,
          grant_type: 'authorization_code',
          redirect_uri: redirect_uri,
          company_id: company_id,
        }),
        headers = {
          'Content-Type': 'application/x-www-form-urlencoded',
        };

      try {
        let response = null;
        response = await this.authAPI[this.authenticationSystem].post(`token`, data.toString(), {
          headers,
        });

        if (response.status === 200) {
          // Set Company ID as default for API calls
          if (company_id) {
            localStorage.setItem('company_id', company_id);
            this.authAPI[this.authenticationSystem].defaults.headers.common['company_id'] = company_id;
          }
          await this.authenticateFromAPIResponse(response);
          return true;
        }
      } catch (e) {
        if (this.authenticationSystem == 'ngd-next-sso') {
          if (e.response.data.messages[0] == 'Access code is invalid.') {
            // this.authenticationSystem = 'neon-sso';
            let response = await this.getNeonSSOToken(code);

            if (response.status === 200) {
              const code = await this.getDonorAccountCodeForNeonSSO(
                response.data.access_token,
                urlParams.get('company_id'),
              );

              return this.authenticateCode(code, urlParams.get('company_id'));
            }
          }
        }
        throw {
          message: e.response?.data?.message ?? 'Could not authenticate using the provided code. Please try again.',
        };
      }

      return false;
    },

    async getNeonSSOToken(code) {
      // Get the company parameter from the URL
      const urlParams = new URLSearchParams(window.location.search);

      const redirect_uri = `${window.location.origin}/authenticate?company_id=${urlParams.get('company_id')}`;
      const data = new URLSearchParams({
        code,
        grant_type: 'authorization_code',
        redirect_uri: redirect_uri,
      });

      const headers = {
        'Content-Type': 'application/x-www-form-urlencoded',
      };

      const response = await this.authAPI['neon-sso'].post(`token`, data.toString(), {
        headers,
      });

      return response;
    },

    async getDonorAccountCodeForNeonSSO(token, company_id) {
      const response = await this.authAPI[this.authenticationSystem].post(
        `token`,
        {
          neonSSOAuth: true,
          company_id: company_id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      return response.data.code;
    },

    async authenticateFromAPIResponse(response) {
      const loginReferrer = document.referrer ? new URL(document.referrer).origin : null;
      await this.setAuthVariables({
        authenticated: true,
        token: response.data.access_token,
        expires: new Date().getTime() / 1000 + parseInt(response.data.expires_in),
        id_token: response.data.id_token,
        refresh_token: response.data.refresh_token,
        client_id: response.data.client_id,
        login_referrer: loginReferrer != window.location.origin ? loginReferrer : null,
        authentication_system: this.authenticationSystem,
      });

      // Keep Checking Local Storage
      this.checkLocalStorage();
      if ('localStorage' in window && window['localStorage'] !== null) {
        setInterval(() => {
          this.checkLocalStorage();
        }, 500);
      }

      if (response.data.redirect_to_portal) {
        window.location.href = response.data.redirect_url;
      }
    },

    async setAuthVariables(data) {
      try {
        localStorage.setItem('token', data.token);
        this.token = data.token;
        localStorage.setItem('expires', data.expires);
        this.expires = data.expires;
        localStorage.setItem('id_token', data.id_token);
        this.id_token = data.id_token;
        localStorage.setItem('refresh_token', data.refresh_token);
        this.refresh_token = data.refresh_token;
        localStorage.setItem('client_id', data.client_id);
        this.client_id = data.client_id;
        localStorage.setItem('login_referrer', data.login_referrer);
        this.login_referrer = data.login_referrer;
        localStorage.setItem('authentication_system', data.authentication_system);

        // Decode access token
        if (this.token) {
          const tokenParts = this.token.split('.');
          if (tokenParts.length === 3) {
            try {
              const base64Url = tokenParts[1];
              const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
              const tokenData = JSON.parse(atob(base64));
              this.type = tokenData.type;
              localStorage.setItem('type', tokenData.type);
            } catch (error) {
              console.error('Error decoding token:', error);
            }
          } else {
            console.error('Invalid token format:', this.token);
          }
        }

        this.authenticated = data.authenticated;

        if (this.authenticated) {
          this.setApiAuthenticationHeaders();
          console.log('Authenticated');
          await useDonorStore().onAuthenticated();
        }

        this.authVariablesSet = true;
      } catch (error) {
        console.error('Error setting auth variables:', error);
        throw error;
      }
    },

    logout(redirect = true) {
      this.authenticated = false;
      localStorage.removeItem('token');
      this.token = null;
      localStorage.removeItem('expires');
      this.expires = null;
      localStorage.removeItem('id_token');
      this.id_token = null;
      localStorage.removeItem('refresh_token');
      this.refresh_token = null;
      localStorage.removeItem('client_id');
      this.client_id = null;

      // if we have a stored login referrer or a HTTP referrer, go back to it
      if (redirect && (this.login_referrer || document.referrer)) {
        window.location.href = (this.login_referrer || new URL(document.referrer).origin) + '/authorize?code=logout';
      }
    },

    async resetPassword(key, newPassword) {
      try {
        const response = await api.post(`reset-password`, {
          key,
          newPassword,
        });
        return response;
      } catch (error) {
        console.error(error);
        return null;
      }
    },

    setApiAuthenticationHeaders() {
      if (this.token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${this.token}`;
      } else {
        delete api.defaults.headers.common['Authorization'];
      }

      // Get Company ID from Local Storage
      const company_id = localStorage.getItem('company_id');
      if (company_id) {
        this.authAPI[this.authenticationSystem].defaults.headers.common['company_id'] = company_id;
      }
    },

    redirectToTokenAuth(next) {
      if (this.authenticationSystem == 'neon-sso') {
        // debugger;
        return;
      }
      let domain = window.location.hostname;

      if (domain == 'localhost') {
        if (next) next('/login');
        return;
      }

      if (window.location.href.includes('authorize')) {
        if (next) next();
        return;
      }

      if (domain == 'donorportal.uat.ngd.computer') {
        domain = 'uat-demo-for-product-development.multiscreensite.com';
      } else if (domain == 'donorportal.prod.ngd.computer') {
        domain = 'cms.prod.ngd.computer';
      } else if (domain && domain.indexOf('donorportal.') == 0) {
        domain = 'www.' + domain.substring(12);
      }

      let path = window.location.href.split(window.location.hostname)[1];
      if (path && path.includes(':3030')) {
        path = path.split(':3030')[1];
      }

      //Remove / from Path
      if (path && path[0] == '/') {
        path = path.substring(1);
      }

      const returnPath = 'ret=' + encodeURIComponent(path);

      window.location.href = `https://${domain}/authorize?checkPortalAuth=true&${returnPath}`;
    },

    // Switch Account
    async switchAccount(email, password) {
      try {
        const response = await api.post(`auth/switch-account-type`, {
          email,
          password,
          redirect_uri: window.location.origin,
          hosts_id: useDonorStore().hosts_id,
          client_id: import.meta.env.VITE_OIDC_CLIENT_ID,
          response_type: 'code',
          scope: 'openid',
          state: Math.random().toString(36),
          nonce: Math.random().toString(36),
        });

        if (response.status == 200) {
          await this.authenticateFromAPIResponse(response);
        } else {
          throw { message: 'Could not switch account. Please try again.' };
        }
      } catch (e) {
        throw e.response?.data?.message ? e.response.data : e;
      }
    },
  },
});
